import { FC, useState } from "react";

import {
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { JunkLoadStatusEnum, PhotoWithCommentType } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsText } from "../components/ui";
import {
  useUpdateAllDriverDumpServicesMutation,
  useUpdateServiceMutation,
} from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import {
  useUpdateAllDriverDumpTasksMutation,
  useUpdateTaskMutation,
} from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";

const DriverAddDumpReceiptPhoto: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const { mutate: uploadServicePhoto, isPending: isUploadingServicePhoto } =
    useUploadServicePhotoMutation();

  const {
    mutate: updateAllDriverDumpServices,

    isPending: isUpdatingServices,
  } = useUpdateAllDriverDumpServicesMutation();

  const {
    mutate: updateAllDriverDumpTasks,
    isPending: isUpdatingTaks,
    variables: allDriverDumpTasksVariables,
  } = useUpdateAllDriverDumpTasksMutation();

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables: taskVariables,
  } = useUpdateTaskMutation();
  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const openCamera = () => setIsCameraOpen(true);

  const closeCamera = () => setIsCameraOpen(false);

  const isDumpAndReturnTask = taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  const uploadPhoto = ({
    base64PhotoString,
    comment,
  }: CameraPhotoWithCommentType) => {
    uploadServicePhoto(
      {
        jobId,
        serviceId,
        base64PhotoString,
        baseName: "dumpReceiptPhotoUrl",
      },
      {
        onSuccess: path => {
          const driverFlowStatus = isDumpAndReturnTask
            ? TaskDriverStatusEnum.DRIVING_BACK_TO_SITE
            : TaskDriverStatusEnum.DUMP_TASKS_REVIEW;

          const dumpReceiptPhotoUrl: PhotoWithCommentType = {
            photoUrl: path,
            comment,
          };

          // Check if there's a list of junk loads
          const junkLoadInProgressIndex = serviceData?.junkLoads?.findIndex(
            junkLoad => junkLoad.status === JunkLoadStatusEnum.IN_PROGRESS,
          );

          // If there's one in progress, means driver is managing data for that specific load
          if (
            junkLoadInProgressIndex !== undefined &&
            junkLoadInProgressIndex !== -1 &&
            serviceData?.junkLoads
          ) {
            const updatedJunkLoad =
              serviceData.junkLoads[junkLoadInProgressIndex];

            updatedJunkLoad.dumpReceiptPhotoUrl = dumpReceiptPhotoUrl;

            serviceData.junkLoads.splice(
              junkLoadInProgressIndex,
              1,
              updatedJunkLoad,
            );

            updateService({
              jobId,
              serviceId,
              driverFlowStatus,
              junkLoads: [...serviceData.junkLoads],
            });

            updateTask({
              jobId,
              serviceId,
              taskId,
              driverFlowStatus,
            });

            return;
          }

          // If it's a dump and return task, update only the current task and service
          if (isDumpAndReturnTask) {
            updateService({
              jobId,
              serviceId,
              driverFlowStatus,
              dumpReceiptPhotoUrl,
            });

            updateTask({
              jobId,
              serviceId,
              taskId,
              driverFlowStatus,
            });

            return;
          }

          //This is the case when driver is driving to dump with multiple tasks
          updateAllDriverDumpServices({
            dumpReceiptPhotoUrl,
            driverFlowStatus,
          });

          updateAllDriverDumpTasks({
            driverFlowStatus,
          });
        },
      },
    );
  };

  const isLoading =
    isUpdatingServices || isUpdatingTaks || isUpdatingTask || isUpdatingService;

  const variables = allDriverDumpTasksVariables ?? taskVariables;

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE;

  const goToPreviousStep = () => {
    // If it's a dump and return task, update only the current task and service
    if (isDumpAndReturnTask) {
      updateService({
        jobId,
        serviceId,
        driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
      });

      return;
    }

    //This is the case when driver is driving to dump with multiple tasks
    updateAllDriverDumpTasks({
      driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
    });

    updateAllDriverDumpServices({
      driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="single-photo"
          onChoosePhoto={photo => {
            setIsCameraOpen(false);
            void uploadPhoto(photo);
          }}
          onCancelClick={closeCamera}
        />
      ) : (
        <>
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsText variant="pageTitle">
              {t("driver_add_dump_receipt_photo.title", {
                stepNumber: isDumpAndReturnTask ? 5 : 3,
              })}
            </DsText>
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isUploadingServicePhoto}
            nextButtonTitle="take_photo"
            previousButtonTitle="driver_add_dump_receipt_photo.back_button"
            onPreviousButtonClick={goToPreviousStep}
            isPreviousButtonLoading={isPreviousButtonLoading}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddDumpReceiptPhoto };
